import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Space, Modal, message, Empty, Tooltip } from 'antd';
import { CheckOutlined, ReloadOutlined, ExclamationCircleOutlined, EditOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../../api/apiClient';

const RectificacionNotas = () => {
  const navigate = useNavigate();
  const [modal, contextHolder] = Modal.useModal();
  const [pendientes, setPendientes] = useState([]);
  const [loading, setLoading]       = useState(false);
  const [completando, setCompletando] = useState(null);

  useEffect(() => { cargarPendientes(); }, []);

  const cargarPendientes = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/solicitudes-calificacion/mis-pendientes');
      setPendientes(res.data.data || []);
    } catch {
      message.error('Error al cargar los pendientes.');
    } finally {
      setLoading(false);
    }
  };

  const marcarCompletado = (record) => {
    modal.confirm({
      title: 'Marcar curso como completado',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>
            ¿Confirmas que ya ingresaste las calificaciones de{' '}
            <strong>{record.NombreAlumno}</strong> en el curso{' '}
            <strong>{record.NombreCurso}</strong>?
          </p>
          <p style={{ color: '#888', fontSize: 12 }}>
            Esta acción no se puede deshacer.
          </p>
        </div>
      ),
      okText: 'Sí, completado',
      cancelText: 'Cancelar',
      onOk: async () => {
        setCompletando(record.IdSolicitudCurso);
        try {
          const res = await apiClient.put(
            `/solicitudes-calificacion/cursos/${record.IdSolicitudCurso}/completar`
          );
          if (res.data.solicitudCompletada) {
            message.success('¡Todos los cursos de esta solicitud han sido completados!');
          } else {
            message.success('Curso marcado como completado.');
          }
          cargarPendientes();
        } catch (e) {
          message.error(e.response?.data?.error || 'Error al marcar como completado.');
        } finally {
          setCompletando(null);
        }
      },
    });
  };

  const columnas = [
    {
      title: 'Alumno',
      key: 'alumno',
      width: 200,
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 500 }}>{r.NombreAlumno}</div>
          <div style={{ fontSize: 12, color: '#888' }}>{r.Matricula}</div>
        </div>
      ),
    },
    {
      title: 'Grado / Sección',
      key: 'grado',
      width: 160,
      render: (_, r) => `${r.NombreGrado} ${r.NombreSeccion} — ${r.NombreJornada}`,
    },
    {
      title: 'Curso',
      dataIndex: 'NombreCurso',
      key: 'curso',
      width: 180,
    },
    {
      title: 'Motivo',
      dataIndex: 'Motivo',
      key: 'motivo',
      render: (m) => (
        <Tooltip title={m}>
          <span style={{ cursor: 'default' }}>
            {m?.length > 60 ? m.substring(0, 60) + '…' : m}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Solicitud #',
      dataIndex: 'IdSolicitud',
      key: 'sol',
      width: 100,
      align: 'center',
      render: (id) => <Tag color="blue">#{id}</Tag>,
    },
    {
      title: 'Fecha',
      dataIndex: 'FechaSolicitud',
      key: 'fecha',
      width: 110,
      render: (f) => f ? f.split('T')[0].split('-').reverse().join('/') : '-',
    },
    {
      title: 'Acciones',
      key: 'acc',
      width: 220,
      align: 'center',
      render: (_, r) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => navigate('/dashboard/docente/rectificacion-calificar', { state: { pendiente: r } })}
          >
            Calificar
          </Button>
          <Button
            type="primary"
            icon={<CheckOutlined />}
            loading={completando === r.IdSolicitudCurso}
            onClick={() => marcarCompletado(r)}
          >
            Completado
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      {contextHolder}
      <Card
        title={<span><CheckOutlined /> Rectificación de Notas</span>}
        extra={
          <Button icon={<ReloadOutlined />} onClick={cargarPendientes} loading={loading}>
            Actualizar
          </Button>
        }
      >
        {!loading && pendientes.length === 0 ? (
          <Empty description="No tienes cursos pendientes de rectificación" />
        ) : (
          <>
            <p style={{ color: '#666', marginBottom: 16 }}>
              El director ha autorizado que ingreses calificaciones para los siguientes alumnos.
              Una vez que hayas registrado las notas en el sistema, marca el curso como completado.
            </p>
            <Table
              columns={columnas}
              dataSource={pendientes}
              rowKey="IdSolicitudCurso"
              loading={loading}
              pagination={{ pageSize: 10, showTotal: t => `Total: ${t} pendiente(s)` }}
              bordered
            />
          </>
        )}
      </Card>
    </div>
  );
};

export default RectificacionNotas;
